import React, { useEffect } from 'react'

import styled from '@emotion/styled'

import PropTypes from 'prop-types'
import {
  useRecoilCallback,
  useRecoilState,
  useRecoilValue,
  useResetRecoilState
} from 'recoil'
import {
  beePluginState,
  editorState,
  openAIState
} from '../recoil/state/editor'
import {
  addMergeTagToWhitelistCallback,
  addNewSaveRowCallback,
  checkForThirdPartyCallback,
  getTotalTokensCountCallback,
  initBeePluginRowCallback,
  saveRowCallback,
  storeUsedTokensCounterCallback
} from '../recoil/callbacks/callbacks'
import { checkForMergeTags, useMergeTagsToast } from 'Hooks/mergeTagWarning'
import { useIntl } from 'react-intl'
import { useParams } from 'react-router-dom'
import { useHistory } from 'react-router'
import { pageState } from '../recoil/state/page'

const BeePluginEditor = ({ idNewRow }) => {
  const intl = useIntl()
  const history = useHistory()
  const checkThirdParty = useRecoilCallback(checkForThirdPartyCallback)
  const { instance: beePluginInstance } = useRecoilValue(beePluginState)
  const initBeePlugin = useRecoilCallback(initBeePluginRowCallback)
  const save = useRecoilCallback(saveRowCallback)
  // const saveAs = useRecoilCallback(saveTemplateAsCallback)
  const [
    { thirdPartyAccepted, mergeTags, mergeTagsWhitelist, exitAfertSave },
    setEditor
  ] = useRecoilState(editorState)
  const resetEditor = useResetRecoilState(editorState)
  const resetPlugin = useResetRecoilState(beePluginState)
  const addMergeTagToWhitelist = useRecoilCallback(
    addMergeTagToWhitelistCallback
  )
  const { totalTokens, remainingTokens, enableUse, showBuy } = useRecoilValue(
    openAIState
  )
  const storeUsedTokensCounter = useRecoilCallback(
    storeUsedTokensCounterCallback,
    []
  )
  const addNewSaveRow = useRecoilCallback(addNewSaveRowCallback, [])
  const getTotalTokensCount = useRecoilCallback(getTotalTokensCountCallback, [])
  const { id } = useParams()
  const { backUrl } = useRecoilValue(pageState)

  useEffect(() => {
    checkThirdParty()

    return () => {
      resetEditor()
      resetPlugin()
    }
  }, [])

  const mergeTagsToast = useMergeTagsToast({
    ignore: tag => addMergeTagToWhitelist(tag)
  })

  useEffect(() => {
    const refreshedUsageSettings = {
      addOns: [
        {
          id: 'ai-integration',
          settings: {
            tokensAvailable: showBuy ? totalTokens : undefined,
            tokensUsed: showBuy ? totalTokens - remainingTokens : undefined,
            tokenLabel: showBuy
              ? intl.formatMessage({
                  id: 'Load.Bee.Tokens'
                })
              : '',
            isPromptDisabled: !enableUse
          }
        }
      ]
    }
    if (beePluginInstance) {
      beePluginInstance.loadConfig(refreshedUsageSettings)
    }
  }, [remainingTokens])

  const handleSave = json => {
    save(json, false).then(resp => {
      if (resp) {
        history.push(backUrl)
      }
    })
  }

  const handleSaveNew = json => {
    addNewSaveRow(json)
  }

  const handleChange = (json, html) => {
    const { valid: mergeTagsFound } = checkForMergeTags(html, {
      ignore: val => val.startsWith('*cnt_')
    })
    mergeTagsToast.use(mergeTagsFound).update()

    setEditor(prev => ({
      ...prev,
      dirty: true,
      saved: false
    }))
  }

  useEffect(() => {
    if (thirdPartyAccepted) {
      const init = async () => {
        await initBeePlugin({
          // id: idNewRow > 0 ? idNewRow : id,
          id: id,
          settings: {
            onSave: handleSave,
            onSaveAsTemplate: handleSaveNew,
            onChange: handleChange,
            // onSaveRow: function(rowJSON, rowHTML, pageJSON) {},
            onInfo: function(data) {
              if (data.code === 1000) {
                const handle = data.detail.handle
                if (handle === 'ai-integration') {
                  storeUsedTokensCounter(data.detail.usage.total_tokens)

                  getTotalTokensCount()
                }
              }
            },
            contentDialog: {},
            hooks: {}
          }
        })
      }
      init()
    }
  }, [thirdPartyAccepted, id])

  useEffect(() => {
    mergeTagsToast.allowed = [...mergeTags, ...mergeTagsWhitelist]
  }, [mergeTags, mergeTagsWhitelist])

  return <BeeEditor id='bee-plugin-container' />
}

BeePluginEditor.propTypes = {
  idNewRow: PropTypes.number
}

export default BeePluginEditor

const BeeEditor = styled.div`
  left: 0;
  position: absolute;
  right: 0;
  bottom: 0;
  top: 60px;

  overflow: hidden;
`
